import { z } from "zod";
import { L30_PREMIUM_PORTAL_CAPABILITIES, PortalCapabilityListSchema, PortalWorkspacePackageKindSchema, PortalWorkspacePurposeSchema, } from "./portal-workspace.js";
/**
 * Portal user schemas — SSOT for portal user shapes.
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod in @l30/shared
 * first; routes import these and use zValidator).
 *
 * Portal users are external customers/clients with magic-link
 * access to a tenant's portal. Distinct from L30 staff (Clerk auth).
 */
// ─── Base Fields ───────────────────────────────────────────
export const PortalUserRoleSchema = z.enum([
    "client_owner",
    "client_member",
    "supplier",
    "adviser",
    "viewer",
]);
const StaffActorSchema = z.string().trim().min(1).max(320);
export const PortalUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().toLowerCase(),
    name: z.string().min(1).max(200),
    role: PortalUserRoleSchema,
    invitedBy: StaffActorSchema,
    invitedAt: z.string().datetime(),
    lastLoginAt: z.string().datetime().nullable(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: StaffActorSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ─── Create / Update (admin-only operations) ───────────────
/**
 * Input for L30 staff creating a portal user. The route's RBAC
 * gates this to staff (per `02-api/rbac.md`); the body is just the
 * portal-user shape.
 *
 * `tenantSlug` is NOT in the body — the API resolves it from the
 * admin's selected tenant context. Hand-supplying it would be a
 * bypass attempt; the schema deliberately omits it.
 */
export const PortalUserCreateSchema = z.object({
    email: z.string().email().toLowerCase(),
    name: z.string().min(1).max(200),
    role: PortalUserRoleSchema.default("viewer"),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const PortalUserUpdateSchema = z
    .object({
    name: z.string().min(1).max(200),
    role: PortalUserRoleSchema,
    metadata: z.record(z.string(), z.unknown()),
})
    .partial();
// ─── Listing / Filtering ───────────────────────────────────
export const PortalUserListQuerySchema = z.object({
    // Default: active only. Admins toggle via flag to see archived rows.
    includeArchived: z.coerce.boolean().default(false),
});
// ─── Admin response shapes ─────────────────────────────────
/**
 * List response for staff admin UI. Wraps the rows in `users` so we
 * can add envelope fields (counts, pagination) later without breaking
 * the contract.
 */
export const PortalUserListResponseSchema = z.object({
    users: z.array(PortalUserSchema),
});
// ─── Public surfaces (portal user themselves) ──────────────
/**
 * Read-only "who am I" response for portal users. No sensitive
 * fields (no metadata, no invited-by). Returned by GET /api/portal/me.
 */
export const PortalMeSchema = z.object({
    email: z.string().email().toLowerCase(),
    name: z.string(),
    role: PortalUserRoleSchema,
    tenantSlug: z.string().min(1).max(64),
    workspaceName: z.string().min(1).max(200),
    packageKind: PortalWorkspacePackageKindSchema.default("l30_premium"),
    purpose: PortalWorkspacePurposeSchema.default("relationship"),
    capabilities: PortalCapabilityListSchema.default([...L30_PREMIUM_PORTAL_CAPABILITIES]),
    portalEnabled: z.boolean().default(true),
    accentColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .default(null),
    logoBlobKey: z.string().nullable().default(null),
});
//# sourceMappingURL=portal.js.map